import { c as _c } from "react/compiler-runtime";
import { DateFilterChip } from "@components/common/faro-table/faro-table-filter/date-filter/date-filter-chip";
import { DropdownFilterChip } from "@components/common/faro-table/faro-table-filter/dropdown-filter-chip";
import { PROJECTS_ACCESS_LEVEL_FILTER_COLUMN, PROJECTS_DATE_FILTER_COLUMNS, PROJECTS_FILTER_COLUMNS, PROJECTS_LOCATION_FILTER_COLUMN, PROJECTS_LOCATION_FILTER_LABEL, PROJECTS_SINGLE_FILTER_COLUMNS, PROJECTS_SIZE_FILTER_COLUMN, PROJECTS_TAGS_FILTER_COLUMN } from "@components/projects/projects-filters/projects-filter-constants";
import { getProjectsAccessLevelFilterOptions, getProjectsDateFilterPresets, getProjectsFilterOptions } from "@components/projects/projects-filters/projects-filter-options";
import { LocationFilterChip } from "@components/projects/projects-filters/location-filter/location-filter-chip";
import { getProjectsSizeFilterPresets } from "@components/projects/projects-filters/projects-size-filter-utils";
import { SizeFilterChip } from "@components/projects/projects-filters/size-filter/size-filter-chip";
import { TagsFilterChip } from "@components/projects/projects-filters/tags-filter/tags-filter-chip";
import { PROJECTS_TABLE_COLUMN_LABELS } from "@components/table/projects/projects-table-constants";
import { useProjectsTableContext } from "@components/table/projects/use-projects-table-context";
import { doesColumnPassAnyFilter } from "@hooks/table-filters/use-apply-filter-utils";
import { useFilterQueryParams } from "@hooks/table-filters/use-filter-query-params";
import { appStateSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";
import { TableType } from "@utils/track-event/track-event-list";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/** The filters the user applied to a column, which the column offers */

/**
 * Applies the access level, date, size, location and tags filters to the projects and returns the
 * chips to change them. The tags filter is in beta, so without it its query params are ignored.
 * The API cannot filter the projects by them, so it only works on projects that were all fetched
 * at once.
 */
export function useProjectsFilters(t0) {
  const $ = _c(32);
  const {
    projectArchivingState
  } = t0;
  const {
    getFilterIdsForColumn
  } = useFilterQueryParams();
  let t1;
  if ($[0] !== projectArchivingState) {
    t1 = {
      projectArchivingState
    };
    $[0] = projectArchivingState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const contextType = useProjectsTableContext(t1);
  const tableType = contextType === "active projects" ? TableType.activeProjects : TableType.archiveProjects;
  const {
    isBetaTestingEnabled
  } = useAppSelector(appStateSelector);
  let t2;
  if ($[2] !== isBetaTestingEnabled) {
    t2 = column => isBetaTestingEnabled || column !== PROJECTS_TAGS_FILTER_COLUMN;
    $[2] = isBetaTestingEnabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const filterColumns = PROJECTS_FILTER_COLUMNS.filter(t2);
  let t3;
  if ($[4] !== getFilterIdsForColumn) {
    t3 = column_0 => {
      const filterIds = getFilterIdsForColumn(column_0);
      return {
        column: column_0,
        filterIds,
        activeFilterIds: getKnownFilterIds(column_0, filterIds)
      };
    };
    $[4] = getFilterIdsForColumn;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const filtersByColumn = filterColumns.map(t3);
  const isAnyFilterApplied = filtersByColumn.some(_temp);
  const hasUnknownFilterIds = filtersByColumn.some(_temp2);
  const [, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!hasUnknownFilterIds) {
      return;
    }
    setSearchParams(searchParams => {
      filterColumns.forEach(column_1 => {
        const filterIds_1 = searchParams.getAll(column_1);
        const knownFilterIds = getKnownFilterIds(column_1, filterIds_1);
        if (knownFilterIds.length === filterIds_1.length) {
          return;
        }
        searchParams.delete(column_1);
        knownFilterIds.forEach(filterId => searchParams.append(column_1, filterId));
      });
      return searchParams;
    }, {
      replace: true
    });
  }, [filterColumns, hasUnknownFilterIds, setSearchParams]);
  const activeFiltersKey = JSON.stringify(filtersByColumn.filter(_temp3).map(_temp4));
  let t4;
  if ($[6] !== activeFiltersKey) {
    t4 = projects => {
      const activeFilters = JSON.parse(activeFiltersKey);
      if (activeFilters.length === 0) {
        return projects;
      }
      const columnFilters = activeFilters.map(_temp5);
      return projects.filter(project => columnFilters.every(columnFilter => doesColumnPassAnyFilter({
        entity: project,
        ...columnFilter
      })));
    };
    $[6] = activeFiltersKey;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const filterProjects = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = getProjectsAccessLevelFilterOptions();
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  let t7;
  if ($[9] !== tableType) {
    t6 = <DropdownFilterChip key={PROJECTS_ACCESS_LEVEL_FILTER_COLUMN} label={PROJECTS_TABLE_COLUMN_LABELS.accessLevel} filteredColumn={PROJECTS_ACCESS_LEVEL_FILTER_COLUMN} items={t5} shouldShowSearchBox={false} shouldShowCheckbox={true} shouldAllowLabelTranslation={true} tableType={tableType} />;
    t7 = Object.fromEntries(PROJECTS_DATE_FILTER_COLUMNS.map(column_4 => [column_4, <DateFilterChip key={column_4} label={PROJECTS_TABLE_COLUMN_LABELS[column_4]} filteredColumn={column_4} presets={getProjectsDateFilterPresets(column_4)} tableType={tableType} />]));
    $[9] = tableType;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = getProjectsSizeFilterPresets();
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== tableType) {
    t9 = <SizeFilterChip key={PROJECTS_SIZE_FILTER_COLUMN} label={PROJECTS_TABLE_COLUMN_LABELS.size} filteredColumn={PROJECTS_SIZE_FILTER_COLUMN} presets={t8} tableType={tableType} />;
    $[13] = tableType;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== projectArchivingState || $[16] !== tableType) {
    t10 = <LocationFilterChip key={PROJECTS_LOCATION_FILTER_COLUMN} label={PROJECTS_LOCATION_FILTER_LABEL} filteredColumn={PROJECTS_LOCATION_FILTER_COLUMN} projectArchivingState={projectArchivingState} tableType={tableType} />;
    $[15] = projectArchivingState;
    $[16] = tableType;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== isBetaTestingEnabled || $[19] !== projectArchivingState || $[20] !== tableType) {
    t11 = isBetaTestingEnabled ? <TagsFilterChip key={PROJECTS_TAGS_FILTER_COLUMN} label={PROJECTS_TABLE_COLUMN_LABELS.tags} filteredColumn={PROJECTS_TAGS_FILTER_COLUMN} projectArchivingState={projectArchivingState} tableType={tableType} /> : null;
    $[18] = isBetaTestingEnabled;
    $[19] = projectArchivingState;
    $[20] = tableType;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t6 || $[25] !== t7 || $[26] !== t9) {
    t12 = {
      [PROJECTS_ACCESS_LEVEL_FILTER_COLUMN]: t6,
      ...t7,
      [PROJECTS_SIZE_FILTER_COLUMN]: t9,
      [PROJECTS_LOCATION_FILTER_COLUMN]: t10,
      [PROJECTS_TAGS_FILTER_COLUMN]: t11
    };
    $[22] = t10;
    $[23] = t11;
    $[24] = t6;
    $[25] = t7;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const filterChips = t12;
  let t13;
  if ($[28] !== filterChips || $[29] !== filterProjects || $[30] !== isAnyFilterApplied) {
    t13 = {
      isAnyFilterApplied,
      filterProjects,
      filterChips
    };
    $[28] = filterChips;
    $[29] = filterProjects;
    $[30] = isAnyFilterApplied;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}

/**
 * Returns the IDs that are filters the column offers. A column that applies only one filter keeps
 * the first of them, as its chip shows only that one.
 */
function _temp5(t0) {
  const [column_3, activeFilterIds_3] = t0;
  return {
    activeFilterIds: activeFilterIds_3,
    allPossibleFiltersForColumn: getProjectsFilterOptions(column_3, activeFilterIds_3)
  };
}
function _temp4(t0) {
  const {
    column: column_2,
    activeFilterIds: activeFilterIds_2
  } = t0;
  return [column_2, activeFilterIds_2];
}
function _temp3(t0) {
  const {
    activeFilterIds: activeFilterIds_1
  } = t0;
  return activeFilterIds_1.length > 0;
}
function _temp2(t0) {
  const {
    filterIds: filterIds_0,
    activeFilterIds: activeFilterIds_0
  } = t0;
  return activeFilterIds_0.length < filterIds_0.length;
}
function _temp(t0) {
  const {
    activeFilterIds
  } = t0;
  return activeFilterIds.length > 0;
}
function getKnownFilterIds(column, filterIds) {
  const filters = getProjectsFilterOptions(column, filterIds);
  const knownFilterIds = filterIds.filter(filterId => filterId in filters);
  return PROJECTS_SINGLE_FILTER_COLUMNS.includes(column) ? knownFilterIds.slice(0, 1) : knownFilterIds;
}